// Presentation only: re-cases the canonical value the tracker stores, folds spellings older
// rows still carry, and pushes the long tail into one row. Nothing stored or targeted changes.

/** Words that read wrong capitalized inside a longer name, across the languages we serve. */
const CONNECTIVES = new Set([
  'av',
  'de',
  'del',
  'della',
  'den',
  'der',
  'des',
  'det',
  'di',
  'du',
  'för',
  'la',
  'le',
  'les',
  'med',
  'met',
  'och',
  'og',
  'på',
  'van',
  'von',
  'and',
  'for',
  'in',
  'of',
  'on',
  'or',
  'the',
  'til',
  'till',
]);

/** A word after one of these starts a new segment. */
const SEGMENT_SEPARATORS = /[/>|]/;
const WORDS = /[^\s/>|]+/g;

/** Letters that carry no decomposed form, so NFD alone would drop them. */
const FOLDED_LETTERS: Record<string, string> = { ø: 'o', æ: 'ae', œ: 'oe', ß: 'ss', đ: 'd', ð: 'd', ł: 'l', þ: 'th' };

/** Accents left by NFD. Matched as a class so the source stays ASCII. */
const COMBINING_MARKS = /\p{M}/gu;

/** Below this a single differing letter says nothing: "black"/"blank", "man"/"dam". */
const NEAR_DUPLICATE_MIN_LENGTH = 6;

/** A size with no vowel is an abbreviation ("xl", "xxs"), unlike "one size" or "slim". */
const SIZE_ABBREVIATION = /^[bcdfghjklmnpqrstvwxz]+$/i;

const MAX_SHOWN_VALUES = 6;
const ALWAYS_SHOWN_VALUES = 3;
const MIN_SHOWN_SHARE_PERCENT = 3;

export interface DimensionValueCount {
  value: string;
  count: number;
}

export interface DimensionCluster {
  /** What the row is labelled with: the strongest member. */
  value: string;
  count: number;
  /** Every raw value folded in, strongest first, the label included. */
  members: string[];
}

export interface DimensionLongTail {
  shown: DimensionCluster[];
  /** Clusters that belong in a single "Other" row, strongest first. */
  hidden: DimensionCluster[];
}

/**
 * Re-cases a canonical value: "gina tricot" becomes "Gina Tricot". A word the site
 * capitalized itself is left alone, and so is any word carrying a digit. A size
 * abbreviation goes fully upper-case.
 */
export function formatDimensionValue(value: string, type?: string): string {
  if (!value) {
    return value;
  }
  if (type === 'size' && SIZE_ABBREVIATION.test(value)) {
    return value.toUpperCase();
  }
  let previousEnd = 0;
  return value.replace(WORDS, (word, offset: number) => {
    const startsSegment = offset === 0 || SEGMENT_SEPARATORS.test(value.slice(previousEnd, offset));
    previousEnd = offset + word.length;
    if (word !== word.toLowerCase() || /\d/.test(word)) {
      return word;
    }
    if (!startsSegment && CONNECTIVES.has(word)) {
      return word;
    }
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
}

/**
 * Drops a proportion, so "Elastan 5%" and "Elastan 1%" read as one interest. Only rows
 * collected before the tracker did this still carry one. A bare proportion keeps it.
 */
export function stripProportions(value: string): string {
  const stripped = value
    .replace(/\(?\s*\d+([.,]\d+)?\s*%\s*\)?/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped || value;
}

/** The key two values are compared on: case, accents and punctuation gone, separators unified to "/". */
export function dimensionComparisonKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/[øæœßđðłþ]/g, (letter) => FOLDED_LETTERS[letter])
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(/[>|]/g, '/')
    .replace(/[^a-z0-9/]+/g, ' ')
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Folds values naming the same thing into one row, strongest label winning: same comparison
 * key, a parent path weaker than its own child, or one word spelled two ways.
 */
export function clusterDimensionValues(values: DimensionValueCount[]): DimensionCluster[] {
  const clusters: (DimensionCluster & { key: string })[] = [];
  for (const { value, count } of [...values].sort((a, b) => b.count - a.count)) {
    const key = dimensionComparisonKey(value);
    const target = clusters.find(
      (cluster) =>
        cluster.key === key || (key !== '' && cluster.key.startsWith(key + '/')) || isNearDuplicate(key, cluster.key),
    );
    if (target) {
      target.count += count;
      target.members.push(value);
    } else {
      clusters.push({ key, value, count, members: [value] });
    }
  }
  return clusters.map(({ value, count, members }) => ({ value, count, members })).sort((a, b) => b.count - a.count);
}

/**
 * Groups values by the label they render as, first-seen order on both levels. Whatever
 * acts on a row has to act on every spelling behind it.
 */
export function groupValuesByDisplay(values: string[], display: (value: string) => string): Map<string, string[]> {
  const grouped = new Map<string, string[]>();
  for (const value of values) {
    const label = display(value);
    const existing = grouped.get(label);
    if (existing) {
      existing.push(value);
    } else {
      grouped.set(label, [value]);
    }
  }
  return grouped;
}

/**
 * Splits ranked clusters into rows worth their own colour and the "Other" tail. A tail
 * of one stays put, since hiding a single value tells the reader less than showing it.
 */
export function splitDimensionLongTail(clusters: DimensionCluster[]): DimensionLongTail {
  const total = clusters.reduce((sum, cluster) => sum + cluster.count, 0);
  const shown: DimensionCluster[] = [];
  for (const cluster of clusters) {
    const share = total > 0 ? (cluster.count / total) * 100 : 0;
    if (shown.length >= MAX_SHOWN_VALUES || (shown.length >= ALWAYS_SHOWN_VALUES && share < MIN_SHOWN_SHARE_PERCENT)) {
      break;
    }
    shown.push(cluster);
  }
  const hidden = clusters.slice(shown.length);
  if (hidden.length === 1) {
    return { shown: [...shown, hidden[0]], hidden: [] };
  }
  return { shown, hidden };
}

function isNearDuplicate(a: string, b: string): boolean {
  const left = tokenize(a);
  const right = tokenize(b);
  if (left.length === 0 || left.length !== right.length) {
    return false;
  }
  let differing = 0;
  for (let i = 0; i < left.length; i++) {
    if (left[i] === right[i]) {
      continue;
    }
    // Only one word may differ: "parfym/man/..." and "parfym/dam/..." are two shelves.
    if (++differing > 1 || !isNearDuplicateWord(left[i], right[i])) {
      return false;
    }
  }
  return differing === 1;
}

function isNearDuplicateWord(a: string, b: string): boolean {
  if (Math.min(a.length, b.length) < NEAR_DUPLICATE_MIN_LENGTH) {
    return false;
  }
  // "38" and "39", or two article numbers, are one edit apart and are different values.
  if (digitsOf(a) !== digitsOf(b)) {
    return false;
  }
  return isOneEditApart(a, b);
}

function tokenize(key: string): string[] {
  return key.split(/[^a-z0-9]+/).filter(Boolean);
}

function digitsOf(word: string): string {
  return word.replace(/\D+/g, '');
}

/** True when one insert, delete or substitution turns `a` into `b`. */
function isOneEditApart(a: string, b: string): boolean {
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (longer.length - shorter.length > 1) {
    return false;
  }
  let i = 0;
  while (i < shorter.length && shorter[i] === longer[i]) {
    i++;
  }
  if (shorter.length === longer.length) {
    return shorter.slice(i + 1) === longer.slice(i + 1);
  }
  return shorter.slice(i) === longer.slice(i + 1);
}
